import { useState } from "react";

import { formatBytes } from "lib/webrtc/debugFormat";
import { HISTORY_POINTS, type RateMetric, type RatePoint } from "lib/webrtc/debugStats";

const WINDOW_MS = HISTORY_POINTS * 1000;
const VIEW_W = 300;
const VIEW_H = 40;

type MetricSpec = {
    metric: RateMetric;
    label: string;
    format: (perSecond: number) => string;
};

const formatFrames = (perSecond: number) => `${perSecond.toFixed(1)}/s`;

// Different units, so each metric gets its own chart and y-scale; never one dual-axis plot.
const METRICS: MetricSpec[] = [
    { metric: "bytesReceived", label: "Bytes received", format: (v) => `${formatBytes(v)}/s` },
    { metric: "framesReceived", label: "Frames received", format: formatFrames },
    { metric: "framesDecoded", label: "Frames decoded", format: formatFrames },
    { metric: "iFrames", label: "I-frames", format: formatFrames },
    { metric: "pFrames", label: "P-frames", format: formatFrames },
];

const nearestIndex = (points: RatePoint[], t: number): number => {
    let best = 0;
    points.forEach((point, index) => {
        if (Math.abs(point.t - t) < Math.abs(points[best].t - t)) best = index;
    });
    return best;
};

type RateChartProps = {
    spec: MetricSpec;
    points: RatePoint[];
    windowEnd: number;
    hoverIndex: number | null;
    onHover: (index: number | null) => void;
};

const RateChart: React.FC<RateChartProps> = ({ spec, points, windowEnd, hoverIndex, onHover }) => {
    const windowStart = windowEnd - WINDOW_MS;
    const values = points.map((point) => point[spec.metric]);
    const ceiling = Math.max(...values, 1e-9) * 1.1;
    const xPercent = (t: number) => ((t - windowStart) / WINDOW_MS) * 100;
    const yPercent = (value: number) => 100 - (value / ceiling) * 100;
    const path = points
        .map((point, index) => `${index === 0 ? "M" : "L"}${(xPercent(point.t) / 100) * VIEW_W},${(yPercent(point[spec.metric]) / 100) * VIEW_H}`)
        .join(" ");

    const shown = hoverIndex !== null ? points[hoverIndex] : points[points.length - 1];

    const onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        const box = event.currentTarget.getBoundingClientRect();
        const fraction = (event.clientX - box.left) / box.width;
        onHover(nearestIndex(points, windowStart + fraction * WINDOW_MS));
    };

    return (
        <div className="px-3 py-1">
            <div className="flex items-baseline justify-between text-h5">
                <span className="text-black40">{spec.label}</span>
                <span className="font-mono tabular-nums text-white">{shown ? spec.format(shown[spec.metric]) : "—"}</span>
            </div>
            <div
                className="relative mt-0.5 h-10 cursor-crosshair"
                onMouseMove={onMouseMove}
                onMouseLeave={() => onHover(null)}
            >
                <svg
                    viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    preserveAspectRatio="none"
                    className="absolute inset-0 !h-full !w-full text-primary2"
                    aria-hidden
                >
                    <line x1="0" x2={VIEW_W} y1={VIEW_H} y2={VIEW_H} className="stroke-black80" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    <line x1="0" x2={VIEW_W} y1={VIEW_H / 2} y2={VIEW_H / 2} className="stroke-black80/60" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    {path ? (
                        <path d={path} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    ) : null}
                </svg>
                {hoverIndex !== null && points[hoverIndex] ? (
                    <>
                        <div
                            className="pointer-events-none absolute bottom-0 top-0 w-px bg-black40"
                            style={{ left: `${xPercent(points[hoverIndex].t)}%` }}
                        />
                        <div
                            className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-black90 bg-primary2"
                            style={{
                                left: `${xPercent(points[hoverIndex].t)}%`,
                                top: `${yPercent(points[hoverIndex][spec.metric])}%`,
                            }}
                        />
                    </>
                ) : null}
            </div>
        </div>
    );
};

type Props = {
    history: RatePoint[];
};

/** Per-second rates for the selected stream over the last two minutes. */
const DevWebrtcStatsCharts: React.FC<Props> = ({ history }) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    if (history.length === 0) {
        return <p className="px-3 py-2 text-h5 text-black40">Rates appear after two stats samples from a connected stream.</p>;
    }

    const windowEnd = history[history.length - 1].t;
    const hovered = hoverIndex !== null ? history[hoverIndex] : null;

    return (
        <div className="py-1">
            {METRICS.map((spec) => (
                <RateChart
                    key={spec.metric}
                    spec={spec}
                    points={history}
                    windowEnd={windowEnd}
                    hoverIndex={hoverIndex}
                    onHover={setHoverIndex}
                />
            ))}
            <div className="flex justify-between px-3 text-[11px] tabular-nums text-black60">
                <span>−{HISTORY_POINTS} s</span>
                <span>{hovered ? `${Math.round((windowEnd - hovered.t) / 1000)} s ago` : "now"}</span>
            </div>
        </div>
    );
};

export default DevWebrtcStatsCharts;
